// ============================================================================
//  dataCache.js — background-refresh cache (never expires, always revalidates)
// ============================================================================
//
//  Philosophy: the user should NEVER see a blank screen. If we've ever
//  loaded this URL successfully — even days ago — we show that immediately
//  and quietly refresh in the background. When the fresh response arrives,
//  the UI updates via onUpdate.
//
//  Survival across reloads + sessions: every successful response is
//  mirrored to localStorage under a `dc:<url>` key. On boot, that's
//  hydrated back into the in-memory Map so the first paint after a cold
//  browser open is instant.
//
//  Use in a component:
//
//    cachedFetch(url, { onUpdate: (fresh) => setData(fresh) })
//      .then(setData);    // immediate cached or fresh result
//    // onUpdate fires later when the background refresh produces
//    // newer data — UI silently catches up.
//
//  Mutations (approve, bulk edit, etc.) call bust(url) / bustPrefix(prefix)
//  to force the next read to wait on a fresh fetch instead of returning
//  the now-stale cached value.
// ============================================================================

const LS_PREFIX = 'dc:';
const MAX_LS_BYTES_PER_KEY = 2 * 1024 * 1024;   // skip persisting responses > 2 MB

// In-process map: url -> { value, fetchedAt, inflight }
const CACHE = new Map();

// ── localStorage helpers ───────────────────────────────────────────────────
function lsGet(url) {
  try {
    const raw = localStorage.getItem(LS_PREFIX + url);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch { return null; }
}
function lsSet(url, value, fetchedAt) {
  try {
    const payload = JSON.stringify({ value, fetchedAt });
    if (payload.length > MAX_LS_BYTES_PER_KEY) {
      console.warn('[dataCache] skipping localStorage write for ' + url + ' (' + Math.round(payload.length / 1024) + ' KB > ' + Math.round(MAX_LS_BYTES_PER_KEY / 1024) + ' KB cap)');
      return;
    }
    localStorage.setItem(LS_PREFIX + url, payload);
  } catch {
    // Quota exceeded — drop the oldest dc:* entries
    try {
      const keys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(LS_PREFIX)) keys.push(k);
      }
      // Remove first 25% — crude but effective at unblocking writes
      for (let i = 0; i < Math.ceil(keys.length / 4); i++) {
        localStorage.removeItem(keys[i]);
      }
    } catch {}
  }
}
function lsDel(url) {
  try { localStorage.removeItem(LS_PREFIX + url); } catch {}
}
function lsDelPrefix(prefix) {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(LS_PREFIX + prefix)) keys.push(k);
    }
    for (const k of keys) localStorage.removeItem(k);
  } catch {}
}

// ── Network ────────────────────────────────────────────────────────────────
async function rawFetchJson(url) {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error(res.status + ' ' + res.statusText);
  return res.json();
}

// ── Public API ─────────────────────────────────────────────────────────────
export async function cachedFetch(url, opts) {
  const o = opts || {};
  const onUpdate = o.onUpdate;
  let entry = CACHE.get(url);

  // Hydrate from localStorage on first access this session.
  if (!entry) {
    const persisted = lsGet(url);
    if (persisted) {
      entry = { value: persisted.value, fetchedAt: persisted.fetchedAt || 0, inflight: null };
      CACHE.set(url, entry);
    }
  }

  // We have SOME value (either in memory or hydrated from LS) → return it
  // immediately AND kick off a background refresh.
  if (entry && entry.value !== undefined) {
    if (!entry.inflight) {
      if (o.onRefreshStart) { try { o.onRefreshStart(); } catch {} }
      entry.inflight = rawFetchJson(url)
        .then(fresh => {
          const prev = entry.value;
          entry.value = fresh;
          entry.fetchedAt = Date.now();
          entry.inflight = null;
          lsSet(url, fresh, entry.fetchedAt);
          if (onUpdate && JSON.stringify(fresh) !== JSON.stringify(prev)) {
            onUpdate(fresh);
          }
          if (o.onRefreshEnd) { try { o.onRefreshEnd(true); } catch {} }
          return fresh;
        })
        .catch(e => {
          entry.inflight = null;
          // Keep showing the stale value — don't propagate the error.
          console.warn('[dataCache] background refresh failed for ' + url + ': ' + (e && e.message));
          if (o.onRefreshEnd) { try { o.onRefreshEnd(false); } catch {} }
        });
    }
    return entry.value;
  }

  // No prior data anywhere — must wait for a real fetch.
  if (entry && entry.inflight) return entry.inflight;
  const promise = rawFetchJson(url);
  entry = { value: undefined, fetchedAt: 0, inflight: promise };
  CACHE.set(url, entry);
  try {
    const val = await promise;
    entry.value = val;
    entry.fetchedAt = Date.now();
    entry.inflight = null;
    lsSet(url, val, entry.fetchedAt);
    return val;
  } catch (e) {
    CACHE.delete(url);
    throw e;
  }
}

// Bust one URL — forces the next read to wait on a fresh fetch.
export function bust(url) {
  CACHE.delete(url);
  lsDel(url);
}
export function bustAll() {
  CACHE.clear();
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(LS_PREFIX)) keys.push(k);
    }
    for (const k of keys) localStorage.removeItem(k);
  } catch {}
}
export function bustPrefix(prefix) {
  for (const k of Array.from(CACHE.keys())) {
    if (k.startsWith(prefix)) CACHE.delete(k);
  }
  lsDelPrefix(prefix);
}
